import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Box, CircularProgress, Grid, LinearProgress, Paper, Typography, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import Loader from '../../../components/Loader';
import {
    exportBillablesCSVService,
    importBillablesCSVService,
    getCSVImportJobStatus,
    getActiveBillablesImportJobId,
    getInsurancesService,
    exportAssignmentsCSVService,
    importAssignmentsCSVService,
    createInsuranceService,
} from '../../../services/billing';
import { IInsurance } from '../../../constants/types';
import { downloadCsv } from '../../../utils/csvDownload';
import { useSnackBarState } from '../../../hooks';
import { Translate } from 'react-localize-redux';
import SkippedBillablesNotice, { SkippedBillable } from './SkippedBillablesNotice';
import BillablesConfigurationTabs from './BillablesConfigurationTabs';
import { PriceGridRow, parsePriceCsv, generatePriceCsv } from './priceListGridModel';
import { AssignmentRow, generateAssignmentsCsv, parseAssignmentsCsv } from './insurerAssignmentGridModel';

interface BillablesConfigurationProps {
    uuidInvestigation: string;
    idBillingInfo: number;
    onBillablesUpdated: () => void;
}

/**
 * SHER-1128 (SHER-308 Phase 4) — the Billing configuration screen.
 *
 * Two tabs: a price grid whose columns are the active PriceLists (price-lists.csv) and an
 * insurer→list assignment grid (assignments.csv). Replaces the former single grid whose columns
 * were one-per-insurer. The connected container: it fetches and parses both CSVs, seeds the two
 * grids, and owns Save/Export/Import for each tab. The grids own their own working state, so
 * after a save we re-seed them by bumping their key; edits flow back up via the change callbacks.
 */
const BillablesConfiguration: React.FC<BillablesConfigurationProps> = ({
    uuidInvestigation,
    idBillingInfo,
    onBillablesUpdated,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    // The grids own their own working state; the container holds the current value (seeded from a
    // fetch/import, then tracked through the change callbacks) to serialize on Save. The grids
    // re-seed from these props on a fetch/import, so there is no key-bump remount.
    const [priceState, setPriceState] = useState<{ lists: string[], rows: PriceGridRow[] }>({ lists: [], rows: [] });
    const [assignState, setAssignState] = useState<AssignmentRow[]>([]);
    // insurer code → display name, from the last fetch; used to label a code imported from a file.
    const [nameByCode, setNameByCode] = useState<Record<string, string>>({});

    const [importStatus, setImportStatus] = useState<{
        state: 'idle' | 'uploading' | 'pending' | 'processing' | 'completed' | 'failed';
        message?: string;
        progress?: number;
        skipped?: SkippedBillable[];
    }>({ state: 'idle' });

    useEffect(() => {
        if (uuidInvestigation && idBillingInfo) {
            fetchAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuidInvestigation, idBillingInfo]);

    const extractErrorMessage = (error: any, defaultMessage: string): string => {
        if (error?.response?.data?.errorMessage) return error.response.data.errorMessage;
        if (error?.response?.data?.message) return error.response.data.message;
        if (error?.response?.data?.error) return error.response.data.error;
        if (error?.response?.data && typeof error.response.data === 'string') return error.response.data;
        if (error?.message) return error.message;
        if (typeof error === 'string') return error;
        return defaultMessage;
    };

    const showErrorSnackbar = useCallback((message: string) => {
        setShowSnackbar({ show: false });
        setTimeout(() => setShowSnackbar({ show: true, message, severity: "error" }), 100);
    }, [setShowSnackbar]);

    const showSuccess = useCallback((message: string) => {
        setShowSnackbar({ show: true, message, severity: "success" });
    }, [setShowSnackbar]);

    // ── Fetch ───────────────────────────────────────────────────────────────

    const fetchAll = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        try {
            setLoading(true);
            const [priceResp, insResp, assignResp] = await Promise.all([
                exportBillablesCSVService(uuidInvestigation, idBillingInfo),
                getInsurancesService(uuidInvestigation).catch(() => ({ status: 0, insurances: [] as IInsurance[] })),
                exportAssignmentsCSVService(uuidInvestigation, idBillingInfo).catch(() => ({ status: 0, csv: '' })),
            ]);

            const price = priceResp.status === 200 ? parsePriceCsv(priceResp.csv) : { lists: [], rows: [] };
            setPriceState(price);

            const names: Record<string, string> = {};
            insResp.insurances.forEach((ins) => { names[ins.code] = ins.name; });
            setNameByCode(names);

            // The assignments CSV is the single source for insurer→list (one row per active insurer);
            // names are joined in from the insurer list above (the CSV carries none).
            const assignmentRows = assignResp.status === 200 ? parseAssignmentsCsv(assignResp.csv, names) : [];
            setAssignState(assignmentRows);
        } catch (error: any) {
            showErrorSnackbar(extractErrorMessage(error, "hospital.billing.billables.error.load"));
        } finally {
            setLoading(false);
        }
    };

    // ── Price import job polling (kept from the per-insurer screen; the per-list
    //    import is still the async BullMQ path) ─────────────────────────────────

    const pollJobStatus = (jobId: string): Promise<any> => {
        const maxAttempts = 600;
        const maxConsecutivePollErrors = 5;
        let attempts = 0;
        let consecutivePollErrors = 0;
        return new Promise<any>((resolve, reject) => {
            const pollInterval = setInterval(async () => {
                attempts++;
                if (attempts > maxAttempts) {
                    clearInterval(pollInterval);
                    setImportStatus({ state: 'failed', message: 'Import timeout - job took too long to complete' });
                    reject(new Error("Import timeout - job took too long to complete"));
                    return;
                }
                try {
                    const status = await getCSVImportJobStatus(uuidInvestigation, jobId);
                    consecutivePollErrors = 0;
                    setImportStatus({ state: status.state, message: status.message, progress: status.progress });
                    if (status.state === 'completed') { clearInterval(pollInterval); resolve(status.result); }
                    else if (status.state === 'failed') { clearInterval(pollInterval); reject(new Error(status.error || status.message || "Import failed")); }
                } catch (error: any) {
                    consecutivePollErrors++;
                    if (consecutivePollErrors >= maxConsecutivePollErrors) {
                        clearInterval(pollInterval);
                        setImportStatus({ state: 'failed', message: 'Error checking import status' });
                        reject(error);
                    }
                }
            }, 1000);
        });
    };

    const finalizePriceImport = async (result: any) => {
        const skipped: SkippedBillable[] = result?.skipped || [];
        setImportStatus({ state: 'completed', message: 'Import completed successfully!', skipped });
        showSuccess("hospital.billing.billables.success.updated");
        onBillablesUpdated();
        await fetchAll();
        if (skipped.length === 0) {
            setTimeout(() => setImportStatus({ state: 'idle' }), 3000);
        }
    };

    // ── Price tab actions ─────────────────────────────────────────────────────

    const handleSavePrices = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        try {
            setSaving(true);
            setImportStatus({ state: 'uploading', message: 'Uploading CSV data...' });
            const csv = generatePriceCsv(priceState.lists, priceState.rows);
            // SHER-1128: the 4th arg is forceCreateInsurance, which in per-list mode force-creates
            // any price-list column the server does not yet have. The interactive grid's "+ List"
            // is an explicit instruction to create a list, so Save must send it as true — with false
            // the API rejects the new column ("PriceList '<code>' does not exist") and the list never
            // persists, and a later insurer→list assignment then fails. (The file-upload path only
            // populates the grid; it reaches the server through this same Save.)
            const response = await importBillablesCSVService(uuidInvestigation, idBillingInfo, csv, true);
            if (response.status === 202 && response.jobId) {
                setImportStatus({ state: 'processing', message: 'Processing import...' });
                const result = await pollJobStatus(response.jobId);
                await finalizePriceImport(result);
            } else if (response.status >= 200 && response.status < 300) {
                setImportStatus({ state: 'completed', message: 'Billables updated successfully!' });
                showSuccess("hospital.billing.billables.success.updated");
                onBillablesUpdated();
                await fetchAll();
                setTimeout(() => setImportStatus({ state: 'idle' }), 3000);
            } else {
                const msg = response.message || "hospital.billing.billables.error.save";
                setImportStatus({ state: 'failed', message: msg });
                showErrorSnackbar(msg);
            }
        } catch (error: any) {
            // SHER-144: the upload may succeed server-side while the client sees a network error.
            const isNetworkError = !error?.response && Boolean(error?.message);
            if (isNetworkError && uuidInvestigation && idBillingInfo) {
                const recoveredJobId = await getActiveBillablesImportJobId(uuidInvestigation, idBillingInfo);
                if (recoveredJobId) {
                    setImportStatus({ state: 'processing', message: 'Import still running — reconnecting…' });
                    try {
                        const result = await pollJobStatus(recoveredJobId);
                        await finalizePriceImport(result);
                        return;
                    } catch { /* fall through */ }
                }
            }
            const msg = extractErrorMessage(error, "hospital.billing.billables.error.save");
            setImportStatus({ state: 'failed', message: msg });
            showErrorSnackbar(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleExportPrices = () => downloadCsv('price-lists.csv', generatePriceCsv(priceState.lists, priceState.rows));

    const handleImportPrices = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setPriceState(parsePriceCsv(e.target?.result as string));
        };
        reader.readAsText(file);
    };

    // ── Assignment tab actions ────────────────────────────────────────────────

    const handleSaveAssignments = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        try {
            setSaving(true);
            // Create the named insurers added in the grid first (the bulk CSV cannot name them).
            const newInsurers = assignState.filter(r => r.isNew && r.insuranceCode?.trim());
            for (const row of newInsurers) {
                const res = await createInsuranceService(uuidInvestigation, {
                    code: row.insuranceCode.trim(),
                    name: (row.name || row.insuranceCode).trim(),
                });
                // 400 "already exists" is fine — assign its list below; any other failure aborts.
                if (res.status >= 400 && !/already exists/i.test(res.errorMessage || '')) {
                    showErrorSnackbar(res.errorMessage || 'hospital.billing.billables.error.save');
                    return;
                }
            }

            const csv = generateAssignmentsCsv(assignState);
            const res = await importAssignmentsCSVService(uuidInvestigation, idBillingInfo, csv);
            if (res.status >= 400) {
                showErrorSnackbar(res.errorMessage || 'hospital.billing.billables.error.save');
                return;
            }
            showSuccess("hospital.billing.billables.success.updated");
            onBillablesUpdated();
            await fetchAll();
        } catch (error: any) {
            showErrorSnackbar(extractErrorMessage(error, "hospital.billing.billables.error.save"));
        } finally {
            setSaving(false);
        }
    };

    const handleExportAssignments = () => downloadCsv('assignments.csv', generateAssignmentsCsv(assignState));

    const handleImportAssignments = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            setAssignState(parseAssignmentsCsv(e.target?.result as string, nameByCode));
        };
        reader.readAsText(file);
    };

    if (loading) return <Loader />;

    if (!idBillingInfo) {
        return (
            <Alert severity="error">
                <Translate id="hospital.billing.no_billing_info" />
            </Alert>
        );
    }

    return (
        <Box>
            {importStatus.state !== 'idle' && (
                <Paper elevation={3} sx={{
                    p: 2, m: 2, mb: 0,
                    backgroundColor: importStatus.state === 'completed' ? '#e8f5e9' : importStatus.state === 'failed' ? '#ffebee' : '#e3f2fd',
                    borderLeft: `4px solid ${importStatus.state === 'completed' ? '#4caf50' : importStatus.state === 'failed' ? '#f44336' : '#2196f3'}`,
                }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            {(importStatus.state === 'uploading' || importStatus.state === 'pending' || importStatus.state === 'processing')
                                ? <CircularProgress size={24} />
                                : importStatus.state === 'completed'
                                    ? <Box sx={{ color: '#4caf50', fontSize: '24px' }}>✓</Box>
                                    : <Box sx={{ color: '#f44336', fontSize: '24px' }}>✗</Box>}
                        </Grid>
                        {(importStatus.state === 'completed' || importStatus.state === 'failed') && (
                            <Grid item>
                                <IconButton size="small" aria-label="dismiss" data-testid="dismiss-import-status" onClick={() => setImportStatus({ state: 'idle' })}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Grid>
                        )}
                        <Grid item xs>
                            <Typography variant="body1" fontWeight="bold">
                                {importStatus.state === 'uploading' && 'Uploading CSV...'}
                                {importStatus.state === 'pending' && 'Import Queued...'}
                                {importStatus.state === 'processing' && 'Processing Import...'}
                                {importStatus.state === 'completed' && 'Import Completed Successfully'}
                                {importStatus.state === 'failed' && 'Import Failed'}
                            </Typography>
                            {importStatus.message && (
                                <Typography variant="body2" color="text.secondary">{importStatus.message}</Typography>
                            )}
                            {importStatus.progress !== undefined && (
                                <Box sx={{ mt: 1 }}>
                                    <LinearProgress variant="determinate" value={importStatus.progress} sx={{ height: 8, borderRadius: 4 }} />
                                    <Typography variant="caption" color="text.secondary">{importStatus.progress}% complete</Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    {(importStatus.state === 'uploading' || importStatus.state === 'pending' || importStatus.state === 'processing') && (
                        <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />
                    )}
                    <SkippedBillablesNotice skipped={importStatus.skipped} />
                </Paper>
            )}

            <BillablesConfigurationTabs
                priceLists={priceState.lists}
                priceRows={priceState.rows}
                assignmentRows={assignState}
                onPriceRowsChange={(rows) => setPriceState(prev => ({ ...prev, rows }))}
                onPriceListsChange={(lists) => setPriceState(prev => ({ ...prev, lists }))}
                onAssignmentRowsChange={setAssignState}
                onSavePrices={handleSavePrices}
                onExportPrices={handleExportPrices}
                onImportPrices={handleImportPrices}
                onSaveAssignments={handleSaveAssignments}
                onExportAssignments={handleExportAssignments}
                onImportAssignments={handleImportAssignments}
            />

            {saving && <LinearProgress />}

            {showSnackbar.show && (
                <Box sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, minWidth: 400 }}>
                    <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity || 'info'} sx={{ width: '100%' }}>
                        {typeof showSnackbar.message === 'string' && showSnackbar.message.startsWith('hospital.')
                            ? <Translate id={showSnackbar.message} />
                            : showSnackbar.message}
                    </Alert>
                </Box>
            )}
        </Box>
    );
};

export default BillablesConfiguration;
